import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ORDERS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { RefreshCw, Package, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  awaiting: "bg-hyd-gold/15 text-hyd-gold border-hyd-gold/50",
  confirmed: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  placed: "bg-hyd-gold/15 text-hyd-gold border-hyd-gold/40",
  packed: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  dispatched: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-hyd-red/15 text-hyd-red border-hyd-red/40",
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  const reorder = (order) => {
    order.items.forEach((it) => {
      // Add each item qty times
      for (let i = 0; i < it.qty; i++) {
        addItem({
          kind: it.kind,
          ref_id: it.ref_id,
          name: it.name,
          unit_price: it.unit_price,
          mrp: it.unit_price,
          image_url: it.image_url,
        });
      }
    });
    toast.success("Items added to your cart");
  };

  return (
    <div data-testid={ORDERS.page} className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="font-display text-3xl font-extrabold">My orders</h1>
      <p className="text-hyd-text2 text-sm mt-1">Reorder last year&apos;s list in one tap.</p>

      {loading ? (
        <div className="py-12 text-center text-hyd-text2">Loading...</div>
      ) : orders.length === 0 ? (
        <div className="mt-8 text-center bg-hyd-panel border border-hyd-border rounded-xl p-10">
          <Package className="w-10 h-10 mx-auto text-hyd-text3" />
          <p className="text-hyd-text2 mt-2">No orders yet.</p>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {orders.map((o) => (
            <div
              key={o.id}
              data-testid={ORDERS.orderRow(o.id)}
              className="bg-hyd-panel border border-hyd-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-hyd-text2">{o.order_number}</span>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[o.status]}`}>
                    {o.status}
                  </span>
                </div>
                <div className="text-sm mt-1">
                  {o.items.length} items · <span className="text-hyd-gold font-bold">{inr(o.total)}</span>
                </div>
                <div className="text-xs text-hyd-text3 mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={ORDERS.reorderBtn(o.id)}
                  onClick={() => reorder(o)}
                  variant="secondary"
                  className="bg-hyd-panel2 border border-hyd-border text-hyd-gold hover:bg-hyd-panel"
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Reorder
                </Button>
                <Link to={`/orders/${o.id}`}>
                  <Button className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim">
                    Details <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
